import { useState } from 'react';
import type { Company } from '@provalia/shared';
import { Badge, Card, Confirm, Empty, ErrorBox, useToast } from '../../../shared/ui';
import { fmtDateTime } from '../../../shared/format';
import { api, tenantPath } from '../../api';
import type { CompanyTestResult } from '../../types';
import type { TenantTabProps } from '../TenantDetail';
import { CompanyModal } from './CompanyModal';

type TestState = { busy: true } | { busy: false; result: CompanyTestResult } | { busy: false; error: unknown };

export function CompaniesCard({ slug, detail, reload }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const companies = detail.tenant.companies;
  const [editing, setEditing] = useState<Company | 'new' | null>(null);
  const [removing, setRemoving] = useState<Company | null>(null);
  const [tests, setTests] = useState<Record<string, TestState>>({});

  const companyPath = (db: string): string => `${tenantPath(slug)}/companies/${encodeURIComponent(db)}`;

  const test = async (db: string): Promise<void> => {
    setTests((t) => ({ ...t, [db]: { busy: true } }));
    try {
      const result = await api.post<CompanyTestResult>(`${companyPath(db)}/test`);
      setTests((t) => ({ ...t, [db]: { busy: false, result } }));
      toast(`Conexión con ${db} correcta`, 'ok');
      // La prueba puede haber leído el CIF de SAP: se refresca la tabla.
      void reload();
    } catch (error) {
      setTests((t) => ({ ...t, [db]: { busy: false, error } }));
    }
  };

  return (
    <Card
      bodyless
      title={
        <div>
          <h2>Sociedades de SAP</h2>
          <p className="subtle">Cada company_db con su usuario del Service Layer. El CIF se lee de SAP y viaja en cada petición.</p>
        </div>
      }
      actions={
        <button className="btn primary" onClick={() => setEditing('new')}>
          Añadir sociedad
        </button>
      }
    >
      {companies.length === 0 ? (
        <Empty icon="＋">
          <p>Este cliente aún no tiene sociedades.</p>
          <p className="subtle">Añade la primera con su company_db, usuario y contraseña del Service Layer, y prueba la conexión.</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company DB</th>
                <th>Nombre</th>
                <th>Usuario SAP</th>
                <th>CIF</th>
                <th>Estado</th>
                <th className="right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => {
                const t = tests[c.companyDb];
                return (
                  <tr key={c.id}>
                    <td className="mono nowrap">{c.companyDb}</td>
                    <td>{c.name}</td>
                    <td className="mono nowrap">
                      {c.slUsername}
                      {!c.hasPassword ? (
                        <div>
                          <Badge tone="bad">sin contraseña</Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row">
                        {c.cif ? <span className="mono">{c.cif}</span> : <Badge tone="warn">sin CIF</Badge>}
                        {c.cifSource ? <Badge plain tone={c.cifSource === 'sap' ? 'ok' : 'info'}>{c.cifSource === 'sap' ? 'de SAP' : 'del panel'}</Badge> : null}
                      </div>
                      {c.cifRejected ? <div className="subtle cell-note">SAP devolvió un CIF no válido: {c.cifRejected}</div> : null}
                      {c.cifCheckedAt ? <div className="subtle cell-note">Comprobado {fmtDateTime(c.cifCheckedAt)}</div> : null}
                    </td>
                    <td>
                      <div className="row">
                        {c.isDefault ? <Badge tone="accent">Principal</Badge> : null}
                        {c.active ? <Badge tone="ok">Activa</Badge> : <Badge>Inactiva</Badge>}
                      </div>
                    </td>
                    <td className="right">
                      <div className="row end actions">
                        <button className="btn sm" onClick={() => void test(c.companyDb)} disabled={t?.busy === true}>
                          {t?.busy ? 'Probando…' : 'Probar conexión'}
                        </button>
                        <button className="btn sm" onClick={() => setEditing(c)}>
                          Editar
                        </button>
                        <button className="btn sm danger" onClick={() => setRemoving(c)} aria-label={`Borrar ${c.companyDb}`}>
                          Borrar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {Object.entries(tests).some(([, t]) => !t.busy) ? (
        <div className="card-body stack">
          {companies.map((c) => {
            const t = tests[c.companyDb];
            if (!t || t.busy) return null;
            return (
              <TestResult
                key={c.id}
                companyDb={c.companyDb}
                state={t}
                onClose={() =>
                  setTests((all) => {
                    const next = { ...all };
                    delete next[c.companyDb];
                    return next;
                  })
                }
              />
            );
          })}
        </div>
      ) : null}

      {editing ? (
        <CompanyModal
          slug={slug}
          company={editing === 'new' ? null : editing}
          isFirst={companies.length === 0}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}

      {removing ? (
        <Confirm
          title={`Borrar la sociedad ${removing.companyDb}`}
          danger
          confirmLabel="Borrar sociedad"
          message={
            <p>
              El portal dejará de leer y escribir en <span className="mono">{removing.companyDb}</span>. Los datos de SAP no se tocan.
              {removing.isDefault ? ' Es la sociedad principal: marca otra como principal después.' : ''}
            </p>
          }
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api.del(companyPath(removing.companyDb));
            toast(`Sociedad ${removing.companyDb} borrada`, 'ok');
            await reload();
          }}
        />
      ) : null}
    </Card>
  );
}

function TestResult({
  companyDb,
  state,
  onClose,
}: {
  companyDb: string;
  state: { busy: false; result: CompanyTestResult } | { busy: false; error: unknown };
  onClose: () => void;
}): JSX.Element {
  const close = (
    <button className="btn ghost sm" onClick={onClose} aria-label="Cerrar resultado">
      ✕
    </button>
  );
  if ('error' in state) {
    return (
      <div className="stack sm">
        <div className="row between">
          <strong>Prueba de {companyDb}: no se pudo conectar</strong>
          {close}
        </div>
        <ErrorBox error={state.error} />
      </div>
    );
  }
  const r = state.result;
  return (
    <div className={`callout ${r.aviso ? 'warn' : 'ok'}`} role="status">
      <div className="row between">
        <div className="title">Prueba de {companyDb}: conexión correcta</div>
        {close}
      </div>
      <dl className="kv">
        <dt>Nombre en SAP</dt>
        <dd>{r.nombreEnSap ?? '—'}</dd>
        <dt>CIF que viaja</dt>
        <dd className="mono">{r.cifQueViaja ?? 'ninguno'}</dd>
        <dt>Origen del CIF</dt>
        <dd>{r.origen === 'sap' ? 'Leído de SAP' : r.origen === 'panel' ? 'Tecleado en el panel' : '—'}</dd>
        {r.sapDevolvio ? (
          <>
            <dt>SAP devolvió</dt>
            <dd className="mono">{r.sapDevolvio}</dd>
          </>
        ) : null}
        {r.descartado ? (
          <>
            <dt>Descartado</dt>
            <dd>{r.descartado}</dd>
          </>
        ) : null}
      </dl>
      {r.aviso ? <p className="test-warning">{r.aviso}</p> : null}
    </div>
  );
}
